import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import classNames from 'classnames';
import uuidV4 from 'uuid/v4';
import moment from 'moment';
import {maps as gmaps} from 'google';
import Alert from '@spothero/ui/alert';
import Button from '@spothero/ui/button';
import GooglePlacesSearchInput from '@spothero/ui/google-places-search-input';
import PoweredByGoogle from '@spothero/ui/powered-by-google';
import TextButton from '@spothero/ui/text-button';
import Tooltip from '@spothero/ui/tooltip';
import DOMUtils from '@spothero/utils/dom';
import EnvironmentUtils from '@spothero/utils/environment';
import StorageUtils from '@spothero/utils/storage';
import SearchAPI from 'api/search';
import WithConfig from 'config/WithConfig';
import DateTimePickers from 'common/date-time-pickers/DateTimePickers';
import RadioButtonGroup from 'common/radio-button-group/RadioButtonGroup';
import {push} from 'router/router-actions';
import {
    setTerm,
    updateTimes
} from 'search-request/search-request-actions';
import {resetSelectedSpot} from 'spot/spot-actions';
import {cancelSpotRequests} from 'spots/spots-actions';
import SHUtils from 'utils/sh-utils';
import SearchUtils from '../search-utils';
import DidYouMeanModal from './DidYouMeanModal';

const generateSearchTypes = ({isMonthly, isAirport, includeAirport}) => {
    const searchTypes = [
        {label: 'Hourly', value: 'daily', checked: !(includeAirport && isAirport) && !isMonthly},
        {label: 'Monthly', value: 'monthly', checked: !(includeAirport && isAirport) && isMonthly}
    ];

    if (includeAirport) {
        searchTypes.push({label: 'Airport', value: 'airport', checked: isAirport});
    }

    return searchTypes;
};

class SearchControls extends Component {
    static propTypes = {
        apiDateTimeFormat: PropTypes.string.isRequired,
        className: PropTypes.string,
        isMonthly: PropTypes.bool.isRequired,
        isAirport: PropTypes.bool,
        includeAirport: PropTypes.bool,
        isEvent: PropTypes.bool.isRequired,
        starts: PropTypes.string,
        ends: PropTypes.string,
        placeholder: PropTypes.string,
        searchButtonText: PropTypes.string,
        searchString: PropTypes.string,
        originalSearchString: PropTypes.string,
        defaultSearchInputValue: PropTypes.string,
        usersLatitude: PropTypes.string,
        usersLongitude: PropTypes.string,
        setSearchTerm: PropTypes.func.isRequired,
        updateSearchTimes: PropTypes.func.isRequired,
        cancelAllSpotRequests: PropTypes.func.isRequired,
        focusSearchInput: PropTypes.bool,
        showTerm: PropTypes.bool,
        datetimeOnly: PropTypes.bool,
        showSpotUnavailable: PropTypes.bool,
        showDateTimes: PropTypes.bool,
        showFindParkingButton: PropTypes.bool,
        isDefaultTimes: PropTypes.bool,
        cloudinary: PropTypes.object.isRequired,
        onSearchClick: PropTypes.func,
        onClosePanelClick: PropTypes.func,
        onSuggestSelect: PropTypes.func,
        clearSelectedSpot: PropTypes.func.isRequired,
        pushTo: PropTypes.func.isRequired,
        searchBoundsSWLat: PropTypes.number,
        searchBoundsSWLng: PropTypes.number,
        searchBoundsNELat: PropTypes.number,
        searchBoundsNELng: PropTypes.number,
        isPrerender: PropTypes.bool,
        disableProTip: PropTypes.bool,
        defaultToFindParkingNearMe: PropTypes.bool,
        city: PropTypes.object,
        destination: PropTypes.object
    };
    static defaultProps = {
        showTerm: true,
        placeholder: 'Address, Event, Venue, Airport, or City',
        searchButtonText: 'Search',
        searchString: '',
        showDateTimes: true
    };

    constructor(props) {
        super(props);

        const {
            isMonthly,
            isAirport,
            includeAirport,
            starts,
            ends,
            searchString,
            searchBoundsSWLat,
            searchBoundsSWLng,
            searchBoundsNELat,
            searchBoundsNELng
        } = props;

        this._isMobile = EnvironmentUtils.isMobileDevice();
        this._mobileModalAnimationTime = 500;
        this._bounds = (searchBoundsSWLat)
            ? new gmaps.LatLngBounds(
                new gmaps.LatLng(searchBoundsSWLat, searchBoundsSWLng),
                new gmaps.LatLng(searchBoundsNELat, searchBoundsNELng)
            )
            : null;

        this.state = {
            starts,
            ends,
            isSearching: false,
            isValidStart: true,
            searchAddressModalResults: null,
            showTimeChangeProTip: false,
            searchTermKey: uuidV4(),
            searchTermItems: generateSearchTypes({isMonthly, isAirport, includeAirport}),
            currentSearch: searchString,
            isSearchEmpty: isEmpty(searchString),
            hasChangedDateTime: false,
            hasChangedDestination: false,
            prevIsMonthly: isMonthly,
            prevIsAirport: isAirport
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {
            isMonthly,
            isAirport,
            includeAirport
        } = nextProps;
        const {
            prevIsMonthly,
            prevIsAirport
        } = prevState;

        if (isMonthly !== prevIsMonthly || isAirport !== prevIsAirport) {
            return {
                searchTermKey: uuidV4(),
                searchTermItems: generateSearchTypes({
                    isMonthly,
                    isAirport,
                    includeAirport
                }),
                searchAddressModalResults: null,
                prevIsMonthly: isMonthly,
                prevIsAirport: isAirport
            };
        }

        return null;
    }

    componentDidMount() {
        const {
            isMonthly,
            isPrerender,
            defaultSearchInputValue,
        } = this.props;

        if (!isPrerender) {
            this._showTipTimeout = setTimeout(() => {
                this.setState({
                    showTimeChangeProTip: (!isMonthly && !StorageUtils.get('pro-tip-change-time') && !DOMUtils.el('.Tooltip'))
                });
            }, 3000);
        }

        if (!isEmpty(defaultSearchInputValue)) {
            // this is all temporary until SSR is in place at which point we can remove this
            // on the home page its possible to type something into the search bar on the pre-render snapshot HTML and it gets saved as a cookie and passed in here
            // if that value exists, we should initialize the GooglePlacesSearchInput with it and show the dropdown menu for suggestions based on what the value is
            this._googleSearch.node.focus();
            this._onSearchInputChange({
                target: {
                    value: defaultSearchInputValue
                }
            });

            if (defaultSearchInputValue.length >= 3) {
                this._googleSearch._suggestionFetch(defaultSearchInputValue); // eslint-disable-line no-underscore-dangle
            }
        }
    }

    componentWillUnmount() {
        if (this._showTipTimeout) {
            clearTimeout(this._showTipTimeout);
        }
    }

    _onClearClick = () => {
        this._onSearchInputChange({
            target: {
                value: ''
            }
        });
    }

    _onSearchTermChange = ({value}) => {
        const {
            city,
            destination
        } = this.props;

        StorageUtils.set('pro-tip-change-time', 1);

        this.setState({
            showTimeChangeProTip: false,
            hasChangedDateTime: this._isMobile
        });

        this.props.setSearchTerm({
            term: value,
            city,
            destination
        });
    }

    _onSearchInputChange = evt => {
        const value = evt.target.value;

        this.setState({
            currentSearch: value,
            isSearchEmpty: isEmpty(value),
            hasChangedDestination: true
        });
    }

    _onSuggestSelect = ({
        label,
        location,
        id,
        place_id: placeId,
        reference,
        url,
    }) => {
        const {
            onSuggestSelect
        } = this.props;

        this._submitSearch({
            search_string: label, // eslint-disable-line camelcase
            latitude: location.lat,
            longitude: location.lng,
            google_places_id: id, // eslint-disable-line camelcase
            google_places_place_id: placeId, // eslint-disable-line camelcase
            google_places_reference: reference // eslint-disable-line camelcase
        });

        if (onSuggestSelect) {
            setTimeout(() => {
                // don't call this until the mobile panel has been allowed sufficient time to close and UI is cleared out
                onSuggestSelect();
            }, this._mobileModalAnimationTime);
        }
    }

    _onDateTimeChange = ({start, end, isValidStart}) => {
        const {
            apiDateTimeFormat,
            isDefaultTimes
        } = this.props;
        const {
            starts,
            ends
        } = this.state;
        const newStarts = start.format(apiDateTimeFormat);
        const newEnds = end.format(apiDateTimeFormat);
        const nextState = {
            starts: newStarts,
            ends: newEnds,
            isValidStart
        };

        if (!isDefaultTimes || (starts !== newStarts || ends !== newEnds)) {
            nextState.showTimeChangeProTip = false;
            nextState.hasChangedDateTime = true;

            StorageUtils.set('pro-tip-change-time', 1);
        }

        this.setState(nextState);
    }

    _onNoAddressFoundModalHidden = result => {
        const {
            onClosePanelClick,
            onSearchClick
        } = this.props;
        const {
            starts,
            ends
        } = this.state;

        this.setState({
            searchAddressModalResults: null
        });

        // if multiple results are shown in the dialog then clicking a result should fire the selection process
        if (!isUndefined(result)) {
            this._onSearchAddressSelected(result);

            if (onSearchClick) {
                onSearchClick({
                    starts,
                    ends
                });
            }
        }

        if (onClosePanelClick) {
            setTimeout(() => {
                // don't call this until the mobile panel has been allowed sufficient time to close and UI is cleared out
                onClosePanelClick();
            }, this._mobileModalAnimationTime);
        }
    }

    _onSearchAddressSelected = results => {
        let data = results;

        if (isArray(results)) {
            if (results.length === 1) {
                data = SearchUtils.getAddressData(results[0]);
            } else {
                data = map(data, result => {
                    return SearchUtils.getAddressData(result);
                });

                // no results or more than one result found, show modal
                this.setState({
                    searchAddressModalResults: data
                });

                return;
            }
        }

        this._submitSearch({
            search_string: data.search_string, // eslint-disable-line camelcase
            latitude: data.latitude,
            longitude: data.longitude,
            google_places_id: data.places_id, // eslint-disable-line camelcase
            google_places_place_id: data.places_place_id, // eslint-disable-line camelcase
            google_places_reference: data.places_reference // eslint-disable-line camelcase
        });
    }

    _onSearchClick = () => {
        const {
            onSearchClick,
            updateSearchTimes,
            defaultToFindParkingNearMe,
            pushTo,
            isMonthly
        } = this.props;
        const {
            starts,
            ends,
            hasChangedDateTime,
            hasChangedDestination
        } = this.state;
        const value = (!isEmpty(this._googleSearch))
            ? this._googleSearch.node.value
            : null;

        if (defaultToFindParkingNearMe && isEmpty(value)) {
            pushTo(`/${(isMonthly) ? 'monthly-' : ''}parking-near-me/`);
        } else {
            if (hasChangedDestination) {
                const geocoder = new gmaps.Geocoder();

                geocoder.geocode({
                    address: value
                }, (res, status) => {
                    let results = res;

                    if (status !== gmaps.GeocoderStatus.OK) {
                        results = [];
                    }

                    this._onSearchAddressSelected(results);
                });
            }

            if (onSearchClick) {
                const searchClickParams = {
                    starts,
                    ends
                };

                if (hasChangedDateTime) {
                    searchClickParams.callback = () => {
                        updateSearchTimes({
                            starts,
                            ends
                        });
                    };
                }

                onSearchClick(searchClickParams);
            } else if (hasChangedDateTime) {
                updateSearchTimes({
                    starts,
                    ends
                });
            }
        }

        this.setState({
            hasChangedDateTime: false,
            hasChangedDestination: false
        });
    }

    _onTimeChangeProTipHidden = () => {
        this.setState({
            showTimeChangeProTip: false
        });

        StorageUtils.set('pro-tip-change-time', 1);
    }

    async _submitSearch(locationData) {
        const {
            isMonthly,
            originalSearchString,
            usersLatitude,
            usersLongitude,
            cancelAllSpotRequests,
            clearSelectedSpot,
            pushTo,
            isDefaultTimes
        } = this.props;
        const {
            starts,
            ends,
            hasChangedDateTime
        } = this.state;
        const params = {
            ...locationData,
            csrfmiddlewaretoken: StorageUtils.get('csrftoken', 'cookie'),
            monthly: isMonthly,
            original_search_string: originalSearchString, // eslint-disable-line camelcase
            users_latitude: usersLatitude, // eslint-disable-line camelcase
            users_longitude: usersLongitude, // eslint-disable-line camelcase
            starts: (isDefaultTimes && !hasChangedDateTime) ? null : starts,
            ends: (isMonthly || (isDefaultTimes && !hasChangedDateTime)) ? null : ends
        };

        this.setState({
            isSearching: true
        });

        cancelAllSpotRequests();

        const {
            data: {
                data
            }
        } = await SearchAPI.cleanParams(params);

        this.setState({
            isSearching: false,
            hasChangedDateTime: false,
            hasChangedDestination: false
        });

        clearSelectedSpot();

        pushTo(data.search_url, {cleanedParams: data});
    }

    render() {
        const {
            className,
            isMonthly,
            isEvent,
            starts,
            ends,
            includeAirport,
            placeholder,
            searchButtonText,
            searchString,
            showTerm,
            showDateTimes,
            datetimeOnly,
            showFindParkingButton,
            showSpotUnavailable,
            cloudinary,
            focusSearchInput,
            defaultSearchInputValue,
            isPrerender,
            disableProTip,
            onClosePanelClick
        } = this.props;
        const {
            isSearching,
            isValidStart,
            searchAddressModalResults,
            showTimeChangeProTip,
            searchTermItems,
            searchTermKey,
            isSearchEmpty,
            hasChangedDateTime,
            currentSearch
        } = this.state;
        const classes = classNames(
            'SearchControls',
            {'SearchControls-with-find-parking': showFindParkingButton},
            {'SearchControls-with-airport': includeAirport},
            {'monthly-results': isMonthly},
            {EventSearch: isEvent},
            {'SearchControls-prerender': isPrerender},
            className
        );
        const searchInputClasses = classNames(
            {'GooglePlacesSearchInput-searching': isSearching}
        );
        const hasChangedSearchString = (currentSearch !== searchString);

        return (
            <div className={classes}>
                <div className="container">
                    {showSpotUnavailable &&
                        <Alert
                            type="danger"
                            className="text-center"
                        >
                            This spot is not available at the selected times. Adjust your times or&nbsp;
                            <TextButton
                                onClick={onClosePanelClick}
                            >
                                view available spots.
                            </TextButton>
                        </Alert>
                    }
                    {showTerm &&
                        <RadioButtonGroup
                            key={searchTermKey}
                            className="SearchTerm"
                            color="secondary"
                            initialItems={searchTermItems}
                            disabled={isSearching}
                            onChange={this._onSearchTermChange}
                        />
                    }
                    {!datetimeOnly &&
                        <GooglePlacesSearchInput
                            className={searchInputClasses}
                            ref={node => { this._googleSearch = node; }}
                            placeholder={placeholder}
                            autoFocus={focusSearchInput}
                            onSuggestionSelect={this._onSuggestSelect}
                            poweredByGoogleFooter={<PoweredByGoogle cloudinary={cloudinary} />}
                            googleMaps={gmaps}
                            bounds={this._bounds}
                            loading={isSearching}
                            defaultValue={defaultSearchInputValue || searchString}
                            disabled={isSearching}
                            onClear={this._onClearClick}
                            onChange={this._onSearchInputChange}
                        />
                    }
                    {showDateTimes &&
                        <div className="DateTime">
                            <div className="SearchControls-datetime">
                                <DateTimePickers
                                    isMonthly={isMonthly}
                                    starts={starts}
                                    ends={ends}
                                    onChange={this._onDateTimeChange}
                                />
                                {!isMonthly &&
                                    <span className="SearchControls-duration">
                                        {SHUtils.prettyDuration(moment(ends).diff(moment(starts)))}
                                    </span>
                                }
                                <Button
                                    className="SearchControls-submit"
                                    color="primary"
                                    disabled={!isValidStart || ((!hasChangedSearchString || isSearchEmpty) && !hasChangedDateTime)}
                                    onClick={this._onSearchClick}
                                >
                                    {searchButtonText}
                                </Button>
                            </div>
                        </div>
                    }
                    {showFindParkingButton &&
                        <Button
                            className="SearchControls-find-parking"
                            color="primary"
                            onClick={this._onSearchClick}
                            disabled={isSearching}
                            loading={isSearching}
                        >
                            Find Parking
                        </Button>
                    }
                </div>
                {searchAddressModalResults &&
                    <DidYouMeanModal
                        results={searchAddressModalResults}
                        onHidden={this._onNoAddressFoundModalHidden}
                    />
                }
                {(!disableProTip && showTimeChangeProTip) &&
                    <Tooltip
                        position="bottom"
                        triggerSelector=".DateTimePickers-transient .Label"
                        tipOffset={6}
                        onHidden={this._onTimeChangeProTipHidden}
                    >
                        Remember to set your times so we can guarantee your reservation.
                    </Tooltip>
                }
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        search: {
            data: {
                searchType,
                isDefaultTimes
            }
        },
        searchRequest: {
            monthly,
            starts,
            ends,
            airport,
            search_string: searchString,
            original_search_string: originalSearchString,
            users_latitude: usersLatitude,
            users_longitude: usersLongitude
        },
        city: {
            data: city
        },
        destination: {
            data: destination
        }
    } = state;
    const {
        search_bounds: {
            sw_lat: searchBoundsSWLat,
            sw_lng: searchBoundsSWLng,
            ne_lat: searchBoundsNELat,
            ne_lng: searchBoundsNELng
        }
    } = city;

    return {
        isMonthly: monthly,
        isEvent: (searchType === 'event'),
        isDefaultTimes,
        starts,
        ends,
        isAirport: airport,
        searchString: (searchString) ? searchString : '',
        originalSearchString,
        usersLatitude,
        usersLongitude,
        searchBoundsSWLat,
        searchBoundsSWLng,
        searchBoundsNELat,
        searchBoundsNELng,
        city,
        destination
    };
};

const mapDispatchToProps = {
    setSearchTerm: setTerm,
    updateSearchTimes: updateTimes,
    cancelAllSpotRequests: cancelSpotRequests,
    clearSelectedSpot: resetSelectedSpot,
    pushTo: push
};

const enhance = compose(
    WithConfig,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(SearchControls);
